import { useState, useEffect, useCallback, useRef } from "react";

const API = "https://api.briolabs.io";

const theme = {
  bg:          "#0F1117",
  card:        "#1A1D27",
  cardHover:   "#22262F",
  border:      "#2A2E3A",
  accent:      "#6C5CE7",
  accentLight: "#A29BFE",
  success:     "#00B894",
  warning:     "#FDCB6E",
  danger:      "#E17055",
  text:        "#E8E8ED",
  textMuted:   "#8B8FA3",
  textDim:     "#5A5E72",
  orange:      "#F97316",
  orangeHover: "#EA580C",
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const getToken = () => localStorage.getItem("biz_token");

// STEP 6: prefer ends_at, fall back to valid_until
function parseTimestamp(ts) {
  if (!ts) return new Date(0);
  return new Date(ts.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00"));
}

// STEP 6: use ends_at
function getDealStatus(offer) {
  if (offer.is_active === 0) return "inactive";
  const expiry = parseTimestamp(offer.ends_at || offer.valid_until);
  if (expiry < new Date()) return "expired";
  if (offer.stock_remaining === 0) return "sold_out";
  if (offer.stock_remaining === -1) return "live_unlimited";
  return "live";
}

function formatCountdown(ms) {
  if (ms <= 0) return "00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0)
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

function formatLogTime(ts) {
  if (!ts) return "-";
  const d = new Date(ts.replace(" ", "T"));
  return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")} `+
         `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

const STATUS_CONFIG = {
  live:          { label: "進行中", bg: "#00B89420", text: theme.success,   dot: theme.success  },
  live_unlimited:{ label: "進行中", bg: "#00B89420", text: theme.success,   dot: theme.success  },
  inactive:      { label: "已停用", bg: "#2A2E3A",   text: theme.textMuted, dot: theme.textDim  },
  expired:       { label: "已結束", bg: "#E1705520", text: theme.danger,    dot: theme.danger   },
  sold_out:      { label: "已售完", bg: "#E1705520", text: theme.danger,    dot: theme.danger   },
};

const DEFAULT_FORM = {
  title: "", description: "", original_price: "", flash_price: "",
  stock_remaining: "20", unlimited: false, max_per_person: "1",
  duration_minutes: "30", duration_custom: "", image_url: "", category: "food",
  base_cost: "",   // client-side only — never sent to server
};

const PRESET_DURATIONS = [5, 10, 15, 30, 60, 120];

const QUICK_TEMPLATES = [
  { label: "清庫存 (15分鐘)",   duration: 15, stock: 0  },
  { label: "離峰促銷 (60分鐘)", duration: 60, stock: 20 },
  { label: "午間限定 (30分鐘)", duration: 30, stock: 10 },
];

// STEP 5
const REASON_CODES = [
  { code: "KITCHEN_OVERLOAD",  label: "廚房超載" },
  { code: "SOLD_OUT_EARLY",    label: "提前售完" },
  { code: "DATA_ENTRY_ERROR",  label: "資料錯誤" },
  { code: "OTHER",             label: "其他" },
];

// ─── Countdown (STEP 6 — ends_at, 4-zone display) ────────────────────────────

function Countdown({ offer, status }) {
  const [remaining, setRemaining] = useState(0);
  const ts = offer.ends_at || offer.valid_until;

  useEffect(() => {
    if (status !== "live" && status !== "live_unlimited") { setRemaining(0); return; }
    const expiry = parseTimestamp(ts);
    const update = () => setRemaining(Math.max(0, expiry.getTime() - Date.now()));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [ts, status]);

  if (status === "inactive") return <span style={{ color: theme.textMuted, fontSize: 13 }}>已停用</span>;
  if (status === "expired")  return <span style={{ color: theme.danger,    fontSize: 13 }}>已結束</span>;
  if (status === "sold_out") return <span style={{ color: theme.danger,    fontSize: 13 }}>已售完</span>;

  const secs = Math.floor(remaining / 1000);

  // < 1 min — red + pulse
  if (remaining < 60_000) {
    return (
      <span style={{ fontFamily:"monospace", fontSize:13, fontWeight:700, color:theme.danger, animation:"fdPulse 1s ease-in-out infinite" }}>
        ⏱ {secs}秒
      </span>
    );
  }
  // 1–5 min — amber
  if (remaining < 300_000) {
    return (
      <span style={{ fontFamily:"monospace", fontSize:13, fontWeight:600, color:theme.warning }}>
        ⏱ {formatCountdown(remaining)}
      </span>
    );
  }
  // > 5 min — muted (HH:MM:SS if > 1h, else MM:SS)
  return (
    <span style={{ fontFamily:"monospace", fontSize:13, fontWeight:600, color:theme.textMuted }}>
      ⏱ {formatCountdown(remaining)}
    </span>
  );
}

// ─── DropdownButton ───────────────────────────────────────────────────────────

function DropdownButton({ label, options, onSelect, danger = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const btnColor = danger ? theme.danger : theme.textMuted;
  return (
    <div style={{ position:"relative" }} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        style={{ display:"flex", alignItems:"center", gap:4, padding:"6px 12px", fontSize:12, fontWeight:500,
          border:`1px solid ${danger ? theme.danger+"60" : theme.border}`, borderRadius:8,
          background:"transparent", color:btnColor, cursor:"pointer" }}
        onMouseEnter={e=>{ e.currentTarget.style.background = danger ? theme.danger+"15" : theme.cardHover; }}
        onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
      >
        {label} ▾
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:200,
          background:theme.card, border:`1px solid ${theme.border}`, borderRadius:8,
          boxShadow:"0 8px 24px rgba(0,0,0,0.4)", minWidth:120, overflow:"hidden" }}>
          {options.map((opt) => (
            <button key={opt.value}
              onClick={() => { onSelect(opt.value); setOpen(false); }}
              style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 14px",
                fontSize:12, color:theme.text, background:"transparent", border:"none", cursor:"pointer" }}
              onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
              onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AuditPanel (STEP 3) ──────────────────────────────────────────────────────

function AuditPanel({ offerId }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API}/api/flash-buy/offers/${offerId}/audit`,
          { headers: { Authorization: `Bearer ${getToken()}` } });
        const data = await res.json();
        if (data.success) setLogs(data.logs || []);
        else setError(data.error || "載入失敗");
      } catch { setError("網路錯誤"); }
      finally { setLoading(false); }
    })();
  }, [offerId]);

  const wrap = { marginTop:10, borderRadius:8, overflow:"hidden",
    border:`1px solid ${theme.border}`, fontSize:12 };

  if (loading) return (
    <div style={{ ...wrap, padding:"12px 16px", color:theme.textMuted }}>載入操作紀錄中…</div>
  );
  if (error) return (
    <div style={{ ...wrap, padding:"12px 16px", color:theme.danger }}>{error}</div>
  );
  if (logs.length === 0) return (
    <div style={{ ...wrap, padding:"12px 16px", color:theme.textDim }}>尚無操作紀錄</div>
  );

  const th = { padding:"8px 12px", textAlign:"left", color:theme.textMuted,
    fontWeight:600, background:theme.cardHover, whiteSpace:"nowrap" };
  const td = { padding:"8px 12px", borderTop:`1px solid ${theme.border}` };

  return (
    <div style={wrap}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr>
            {["時間","操作者","動作","原因"].map(h=>(
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log, i) => (
            <tr key={i}>
              <td style={{ ...td, color:theme.textDim,  fontFamily:"monospace" }}>{formatLogTime(log.created_at)}</td>
              <td style={{ ...td, color:theme.textMuted }}>{log.actor  || "-"}</td>
              <td style={{ ...td, color:theme.text,     fontWeight:500 }}>{log.action || "-"}</td>
              <td style={{ ...td, color:theme.textMuted }}>{log.reason || "manual"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── ReasonDialog (STEP 5) ────────────────────────────────────────────────────

const ACTION_LABEL = { delete:"強制下架", "reset-timer":"重設計時器", restock:"補貨" };

function ReasonDialog({ action, onConfirm, onCancel }) {
  const [code,  setCode]  = useState(REASON_CODES[0].code);
  const [other, setOther] = useState("");

  const confirm = () => {
    const reasonCode = code === "OTHER"
      ? `OTHER:${other.trim() || "無"}`
      : code;
    onConfirm(reasonCode);
  };

  const isDanger  = action.type === "delete";
  const accentClr = isDanger ? theme.danger : theme.orange;
  const valueHint = action.value
    ? ` (${action.value}${action.type === "reset-timer" ? " 分鐘" : action.type === "restock" ? " 份" : ""})`
    : "";

  const inputStyle = {
    width:"100%", padding:"10px 14px", background:"#141720",
    border:`1px solid ${theme.border}`, borderRadius:8,
    color:theme.text, fontSize:13, outline:"none", boxSizing:"border-box",
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:9200, padding:20 }}>
      <div style={{ background:theme.card, borderRadius:16, width:"100%", maxWidth:380,
        border:`1px solid ${theme.border}`, boxShadow:"0 24px 64px rgba(0,0,0,0.7)", padding:28 }}>

        <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:700, color:theme.text }}>
          {ACTION_LABEL[action.type] || action.type} — 操作原因
        </h3>
        <p style={{ fontSize:13, color:theme.textMuted, margin:"0 0 18px", lineHeight:1.5 }}>
          「<span style={{ color:theme.text, fontWeight:600 }}>{action.offer.title}</span>」
          {valueHint}
        </p>

        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
          {REASON_CODES.map(r => (
            <label key={r.code} style={{
              display:"flex", alignItems:"center", gap:10, cursor:"pointer",
              padding:"10px 14px", borderRadius:8, transition:"all 0.15s",
              background: code === r.code ? accentClr+"1A" : "transparent",
              border:`1px solid ${code === r.code ? accentClr : theme.border}`,
            }}>
              <input type="radio" name="fd-reason" value={r.code}
                checked={code === r.code} onChange={() => setCode(r.code)}
                style={{ accentColor: accentClr, margin:0 }} />
              <span style={{ fontSize:13, color:theme.text }}>{r.label}</span>
            </label>
          ))}
        </div>

        {code === "OTHER" && (
          <input type="text" value={other} onChange={e => setOther(e.target.value)}
            placeholder="請輸入原因…"
            style={{ ...inputStyle, marginBottom:16 }} />
        )}

        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onCancel}
            style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${theme.border}`,
              background:"transparent", color:theme.textMuted, fontSize:13, cursor:"pointer" }}
            onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
            onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}>
            取消
          </button>
          <button onClick={confirm}
            style={{ padding:"9px 20px", borderRadius:8, border:"none",
              background: accentClr, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}
            onMouseEnter={e=>{ e.currentTarget.style.opacity = "0.85"; }}
            onMouseLeave={e=>{ e.currentTarget.style.opacity = "1"; }}>
            確認
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function FlashDealsPage({ account }) {
  const venueId = account?.venue_id || "ven_fuduchin_cafe_001";

  const [offers,         setOffers]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [editingOffer,   setEditingOffer]   = useState(null);
  const [form,           setForm]           = useState(DEFAULT_FORM);
  const [formErrors,     setFormErrors]     = useState({});
  const [submitting,     setSubmitting]     = useState(false);
  const [toast,          setToast]          = useState(null);
  const [templateOpen,   setTemplateOpen]   = useState(false);
  // STEP 3 — audit trail
  const [expandedAudit,  setExpandedAudit]  = useState(new Set());
  // STEP 5 — reason dialog (replaces deleteConfirm)
  const [pendingAction,  setPendingAction]  = useState(null);

  const templateRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (templateRef.current && !templateRef.current.contains(e.target)) setTemplateOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchOffers = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/flash-buy/offers/${venueId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) {
        const priority = (s) => (s === "live" || s === "live_unlimited" ? 0 : 1);
        setOffers([...(data.offers ?? [])].sort((a,b) =>
          priority(getDealStatus(a)) - priority(getDealStatus(b))));
      }
    } catch (err) { console.error("Failed to fetch offers:", err); }
    finally { setLoading(false); }
  }, [venueId]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);
  useEffect(() => {
    const id = setInterval(fetchOffers, 30_000);
    return () => clearInterval(id);
  }, [fetchOffers]);

  // ── actions (all accept reasonCode — STEP 5) ───────────────────────────────

  const handleResetTimer = async (offer, mins, reasonCode) => {
    if (!mins || mins <= 0) return;
    try {
      const res  = await fetch(`${API}/api/flash-buy/offers/${offer.id}/reset-timer`, {
        method:"POST",
        headers:{ Authorization:`Bearer ${getToken()}`, "Content-Type":"application/json" },
        body: JSON.stringify({ duration_minutes: Number(mins), reason_code: reasonCode }),
      });
      const data = await res.json();
      if (data.success) { showToast(`計時器已重設為 ${mins} 分鐘`); fetchOffers(); }
      else showToast(data.error || "操作失敗", "error");
    } catch { showToast("網路錯誤", "error"); }
  };

  const handleRestock = async (offer, qty, reasonCode) => {
    if (!qty || qty <= 0) return;
    try {
      const res  = await fetch(`${API}/api/flash-buy/offers/${offer.id}/restock`, {
        method:"POST",
        headers:{ Authorization:`Bearer ${getToken()}`, "Content-Type":"application/json" },
        body: JSON.stringify({ stock: Number(qty), reason_code: reasonCode }),
      });
      const data = await res.json();
      if (data.success) { showToast(`已補貨 ${qty} 份`); fetchOffers(); }
      else showToast(data.error || "操作失敗", "error");
    } catch { showToast("網路錯誤", "error"); }
  };

  const handleDelete = async (offer, reasonCode) => {
    try {
      const res  = await fetch(`${API}/api/flash-buy/offers/${offer.id}`, {
        method:"DELETE",
        headers:{ Authorization:`Bearer ${getToken()}`, "Content-Type":"application/json" },
        body: JSON.stringify({ reason_code: reasonCode }),
      });
      const data = await res.json();
      if (data.success) { showToast("優惠已強制下架"); fetchOffers(); }
      else showToast(data.error || "操作失敗", "error");
    } catch { showToast("網路錯誤", "error"); }
  };

  // STEP 5 — dispatch after reason confirmed
  const handleReasonConfirm = (reasonCode) => {
    const a = pendingAction;
    setPendingAction(null);
    if (a.type === "delete")       handleDelete(a.offer, reasonCode);
    if (a.type === "reset-timer")  handleResetTimer(a.offer, a.value, reasonCode);
    if (a.type === "restock")      handleRestock(a.offer, a.value, reasonCode);
  };

  // ── modal helpers ──────────────────────────────────────────────────────────

  const openCreateModal = (prefill) => {
    setEditingOffer(null);
    const base = { ...DEFAULT_FORM };
    if (prefill) {
      base.duration_minutes = String(prefill.duration);
      if (prefill.stock > 0) base.stock_remaining = String(prefill.stock);
    }
    setForm(base); setFormErrors({}); setShowModal(true);
  };

  const openEditModal = (offer) => {
    setEditingOffer(offer);
    setForm({
      title:           offer.title,
      description:     offer.description || "",
      original_price:  offer.original_price > 0 ? String(offer.original_price) : "",
      flash_price:     String(offer.flash_price),
      stock_remaining: offer.stock_remaining === -1 ? "20" : String(offer.stock_remaining),
      unlimited:       offer.stock_remaining === -1,
      max_per_person:  String(offer.max_per_person),
      duration_minutes:"30",
      duration_custom: "",
      image_url:       offer.image_url || "",
      category:        offer.category || "food",
    });
    setFormErrors({}); setShowModal(true);
  };

  const effectiveDuration = () => {
    const p = Number(form.duration_minutes);
    return PRESET_DURATIONS.includes(p) ? p : Number(form.duration_custom) || 0;
  };

  // STEP 4 — discount guardrails (computed for both validation & render)
  const discountInfo = (() => {
    const fp = Number(form.flash_price);
    const op = Number(form.original_price);
    if (!op || !fp || fp >= op || fp <= 0) return { pct: 0, guard: null };
    const pct = Math.round(((op - fp) / op) * 100);
    return {
      pct,
      guard: pct > 80 ? "block" : pct > 50 ? "warn" : null,
    };
  })();

  // Margin guardrail — client-side only, base_cost never leaves the browser
  const marginInfo = (() => {
    const fp = Number(form.flash_price);
    const bc = Number(form.base_cost);
    if (!bc || bc <= 0 || !fp || fp <= 0) return null;   // skip when cost not set
    const pct        = Number(((fp - bc) / fp * 100).toFixed(1));
    const floorPrice = Math.ceil(bc * 1.15);
    let guard, label;
    if (pct < 0)  { guard = "block"; label = "❌ 賠本銷售！特價低於成本"; }
    else if (pct < 15) { guard = "block"; label = `❌ 低於最低毛利 15%，需主管核准`; }
    else if (pct < 20) { guard = "warn";  label = `⚠️ 毛利 ${pct}%，接近底線`; }
    else               { guard = "ok";    label = `✅ 毛利 ${pct}%，安全`; }
    return { pct, floorPrice, guard, label };
  })();

  const validateForm = () => {
    const errors = {};
    if (!form.title.trim())           errors.title = "優惠名稱必填";
    else if (form.title.length > 50)  errors.title = "優惠名稱不能超過 50 字";

    const fp = Number(form.flash_price);
    if (!form.flash_price || fp <= 0) errors.flash_price = "特價不可為零";
    else if (form.original_price && fp >= Number(form.original_price))
      errors.flash_price = "特價必須低於原價";
    else if (discountInfo.guard === "block")
      errors.flash_price = "折扣超過八折，需主管權限";

    if (!form.unlimited && (!form.stock_remaining || Number(form.stock_remaining) <= 0))
      errors.stock_remaining = "數量必須大於 0，或勾選不限量";

    if (!effectiveDuration()) errors.duration_minutes = "限時必須大於 0";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const body = {
        venue_id:         venueId,
        title:            form.title.trim(),
        flash_price:      Number(form.flash_price),
        stock_remaining:  form.unlimited ? -1 : Number(form.stock_remaining),
        stock_total:      form.unlimited ? -1 : Number(form.stock_remaining),
        max_per_person:   Number(form.max_per_person) || 1,
        duration_minutes: effectiveDuration(),
        category:         form.category,
      };
      if (form.description.trim()) body.description   = form.description.trim();
      if (form.original_price)     body.original_price = Number(form.original_price);
      if (form.image_url.trim())   body.image_url      = form.image_url.trim();

      const url = editingOffer
        ? `${API}/api/flash-buy/offers/${editingOffer.id}`
        : `${API}/api/flash-buy/offers`;
      const res  = await fetch(url, {
        method: editingOffer ? "PUT" : "POST",
        headers:{ Authorization:`Bearer ${getToken()}`, "Content-Type":"application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) { showToast(editingOffer ? "優惠已更新" : "優惠已建立"); setShowModal(false); fetchOffers(); }
      else showToast(data.error || "操作失敗", "error");
    } catch { showToast("網路錯誤", "error"); }
    finally { setSubmitting(false); }
  };

  // STEP 3 — audit toggle
  const toggleAudit = (id) => {
    setExpandedAudit(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── stats (STEP 7) ─────────────────────────────────────────────────────────

  const activeCount  = offers.filter(o => { const s = getDealStatus(o); return s==="live"||s==="live_unlimited"; }).length;
  const endedCount   = offers.filter(o => { const s = getDealStatus(o); return s!=="live"&&s!=="live_unlimited"; }).length;
  const monthRevenue = offers.reduce((sum, o) => {
    if (o.stock_total > 0 && o.stock_remaining >= 0)
      return sum + Math.max(0, o.stock_total - o.stock_remaining) * o.flash_price;
    return sum;
  }, 0);
  const totalOrders    = offers.reduce((sum, o) => sum + (o.stock_sold || 0), 0);
  const totalCapacity  = offers.reduce((sum, o) => sum + (o.stock_total > 0 ? o.stock_total : 0), 0);
  const conversionRate = totalCapacity > 0 ? Math.round((totalOrders / totalCapacity) * 100) : 0;

  const isCustomDuration = !PRESET_DURATIONS.includes(Number(form.duration_minutes));

  // ── shared styles ──────────────────────────────────────────────────────────

  const s = {
    input: { width:"100%", padding:"10px 14px", background:"#141720", border:`1px solid ${theme.border}`,
      borderRadius:8, color:theme.text, fontSize:14, outline:"none", boxSizing:"border-box" },
    inputError: { borderColor: theme.danger },
    label: { fontSize:13, color:theme.textMuted, marginBottom:6, display:"block", fontWeight:500 },
    card:  { background:theme.card, borderRadius:14, border:"1px solid rgba(255,255,255,0.06)", padding:"20px" },
    btnOrange: { display:"flex", alignItems:"center", gap:6, padding:"9px 18px", borderRadius:8,
      border:"none", background:theme.orange, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" },
    btnGhost:  { padding:"9px 18px", borderRadius:8, border:`1px solid ${theme.border}`,
      background:"transparent", color:theme.textMuted, fontSize:13, cursor:"pointer" },
    smallBtn: { display:"flex", alignItems:"center", gap:4, padding:"6px 12px", fontSize:12,
      fontWeight:500, border:`1px solid ${theme.border}`, borderRadius:8,
      background:"transparent", color:theme.textMuted, cursor:"pointer" },
  };

  if (loading) {
    return <div style={{ padding:60, textAlign:"center", color:theme.textMuted }}>載入中…</div>;
  }

  return (
    <div style={{ paddingBottom:40 }}>
      {/* Keyframes */}
      <style>{`
        @keyframes fdPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:20, right:20, zIndex:9999,
          padding:"12px 20px", borderRadius:10, fontSize:13, fontWeight:600,
          background: toast.type==="success" ? theme.success : theme.danger,
          color:"#fff", boxShadow:"0 4px 20px rgba(0,0,0,0.4)" }}>
          {toast.msg}
        </div>
      )}

      {/* Reason Dialog (STEP 5) */}
      {pendingAction && (
        <ReasonDialog
          action={pendingAction}
          onConfirm={handleReasonConfirm}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {/* ── Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:theme.text, margin:0 }}>🔥 限時優惠管理</h1>
          <p style={{ fontSize:12, color:theme.textMuted, margin:"4px 0 0" }}>Flash Deals</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Quick templates */}
          <div style={{ position:"relative" }} ref={templateRef}>
            <button
              onClick={() => setTemplateOpen(!templateOpen)}
              style={{ display:"flex", alignItems:"center", gap:4, padding:"9px 14px", fontSize:13,
                border:`1px solid ${theme.border}`, borderRadius:8, background:"transparent",
                color:theme.textMuted, cursor:"pointer" }}
              onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
              onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
            >快速模板 ▾</button>
            {templateOpen && (
              <div style={{ position:"absolute", right:0, top:"calc(100% + 4px)", zIndex:200,
                background:theme.card, border:`1px solid ${theme.border}`, borderRadius:8,
                boxShadow:"0 8px 24px rgba(0,0,0,0.4)", minWidth:190, overflow:"hidden" }}>
                {QUICK_TEMPLATES.map(t => (
                  <button key={t.label}
                    onClick={() => { openCreateModal({ duration:t.duration, stock:t.stock }); setTemplateOpen(false); }}
                    style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 16px",
                      fontSize:12, color:theme.text, background:"transparent", border:"none", cursor:"pointer" }}
                    onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
                  >{t.label}</button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => openCreateModal()} style={s.btnOrange}
            onMouseEnter={e=>{ e.currentTarget.style.background = theme.orangeHover; }}
            onMouseLeave={e=>{ e.currentTarget.style.background = theme.orange; }}>
            ＋ 新增優惠
          </button>
        </div>
      </div>

      {/* ── Stats (STEP 7 — 5 KPIs) ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12, marginBottom:24 }}>
        {[
          { label:"進行中",   value:activeCount,                        color:theme.success    },
          { label:"已結束",   value:endedCount,                         color:theme.textMuted  },
          { label:"本月營收", value:`NT$${monthRevenue.toLocaleString()}`, color:theme.text    },
          { label:"本月訂單", value:totalOrders,                        color:theme.accentLight},
          { label:"轉換率",   value:`${conversionRate}%`,               color:theme.warning    },
        ].map(({ label, value, color }) => (
          <div key={label} style={s.card}>
            <div style={{ fontSize:10, color:theme.textMuted, marginBottom:6, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Offer Cards ── */}
      {offers.length === 0 ? (
        <div style={{ ...s.card, padding:"60px 20px", textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔥</div>
          <div style={{ color:theme.textMuted, marginBottom:20 }}>尚無限時優惠</div>
          <button onClick={() => openCreateModal()} style={s.btnOrange}
            onMouseEnter={e=>{ e.currentTarget.style.background = theme.orangeHover; }}
            onMouseLeave={e=>{ e.currentTarget.style.background = theme.orange; }}>
            建立第一個優惠
          </button>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {offers.map((offer) => {
            const status      = getDealStatus(offer);
            const statusCfg   = STATUS_CONFIG[status];
            const isLive      = status === "live" || status === "live_unlimited";
            const discountPct = offer.original_price > 0
              ? Math.round(((offer.original_price - offer.flash_price) / offer.original_price) * 100)
              : 0;

            // STEP 2 — 3-state stock
            const reserved  = offer.stock_reserved || 0;
            const sold      = offer.stock_sold      || 0;
            const available = offer.stock_remaining === -1
              ? -1
              : Math.max(0, offer.stock_remaining - reserved - sold);

            const auditOpen = expandedAudit.has(offer.id);

            return (
              <div key={offer.id} style={{
                ...s.card,
                border:`1px solid ${isLive ? theme.orange+"40" : "rgba(255,255,255,0.06)"}`,
                opacity: isLive ? 1 : 0.75,
              }}>
                <div style={{ display:"flex", alignItems:"flex-start", gap:16 }}>
                  {/* Thumbnail */}
                  <div style={{ width:56, height:56, borderRadius:10, background:theme.cardHover,
                    flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                    {offer.image_url
                      ? <img src={offer.image_url} alt={offer.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                      : <span style={{ fontSize:24 }}>🍽️</span>}
                  </div>

                  {/* Body */}
                  <div style={{ flex:1, minWidth:0 }}>
                    {/* Title + badge */}
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                      <span style={{ fontWeight:600, color:theme.text, fontSize:15 }}>{offer.title}</span>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5,
                        padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600,
                        background:statusCfg.bg, color:statusCfg.text }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:statusCfg.dot, display:"inline-block" }} />
                        {statusCfg.label}
                      </span>
                    </div>

                    {offer.description && (
                      <p style={{ fontSize:12, color:theme.textMuted, margin:"0 0 8px",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {offer.description}
                      </p>
                    )}

                    {/* Price row */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                      {offer.original_price > 0 && (
                        <span style={{ fontSize:13, color:theme.textDim, textDecoration:"line-through" }}>
                          NT${offer.original_price.toLocaleString()}
                        </span>
                      )}
                      <span style={{ fontSize:18, fontWeight:700, color:theme.orange }}>
                        NT${offer.flash_price.toLocaleString()}
                      </span>
                      {discountPct > 0 && (
                        <span style={{ padding:"1px 6px", borderRadius:4,
                          background:theme.danger+"20", color:theme.danger, fontSize:11, fontWeight:600 }}>
                          -{discountPct}%
                        </span>
                      )}
                    </div>

                    {/* Countdown row */}
                    <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:10, flexWrap:"wrap" }}>
                      <Countdown offer={offer} status={status} />
                    </div>

                    {/* STEP 2 — 3-state stock display */}
                    <div style={{ display:"flex", gap:14, fontSize:13, marginBottom:12, flexWrap:"wrap" }}>
                      {available === -1 ? (
                        <>
                          <span style={{ color:theme.success, fontWeight:500 }}>不限量</span>
                          <span style={{ color:theme.textDim }}>已售 {sold}</span>
                        </>
                      ) : (
                        <>
                          <span style={{ color: available <= 0 ? theme.danger : available <= 5 ? theme.warning : "#22c55e", fontWeight:500 }}>
                            可售 {available}
                          </span>
                          <span style={{ color:"#f59e0b" }}>鎖定 {reserved}</span>
                          <span style={{ color:theme.textDim }}>已售 {sold}</span>
                          <span style={{ color:theme.textDim }}>(共 {offer.stock_total > 0 ? offer.stock_total : offer.stock_remaining})</span>
                        </>
                      )}
                    </div>

                    {/* Actions row */}
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      {/* STEP 5 — reset-timer opens reason dialog */}
                      <DropdownButton
                        label="重設計時器"
                        options={[5,10,15,30,60].map(m => ({ label:`${m} 分鐘`, value:m }))}
                        onSelect={(v) => setPendingAction({ type:"reset-timer", offer, value:v })}
                      />
                      {/* STEP 5 — restock opens reason dialog */}
                      {offer.stock_remaining !== -1 && (
                        <DropdownButton
                          label="補貨"
                          options={[10,20,30,50].map(n => ({ label:`+${n} 份`, value:n }))}
                          onSelect={(v) => setPendingAction({ type:"restock", offer, value:v })}
                        />
                      )}
                      <button onClick={() => openEditModal(offer)} style={s.smallBtn}
                        onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}>
                        ✏️ 編輯
                      </button>
                      {/* STEP 5 — delete opens reason dialog */}
                      <button
                        onClick={() => setPendingAction({ type:"delete", offer })}
                        style={{ ...s.smallBtn, border:`1px solid ${theme.danger}60`, color:theme.danger }}
                        onMouseEnter={e=>{ e.currentTarget.style.background = theme.danger+"15"; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}>
                        🗑 強制下架
                      </button>
                      {/* STEP 3 — audit toggle */}
                      <button
                        onClick={() => toggleAudit(offer.id)}
                        style={{ ...s.smallBtn, color: auditOpen ? theme.accentLight : theme.textMuted,
                          border:`1px solid ${auditOpen ? theme.accent+"60" : theme.border}`,
                          background: auditOpen ? theme.accent+"12" : "transparent" }}
                        onMouseEnter={e=>{ if(!auditOpen) e.currentTarget.style.background = theme.cardHover; }}
                        onMouseLeave={e=>{ if(!auditOpen) e.currentTarget.style.background = "transparent"; }}>
                        📋 操作紀錄
                      </button>
                    </div>

                    {/* STEP 3 — audit panel */}
                    {auditOpen && <AuditPanel offerId={offer.id} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:9000, padding:20 }}>
          <div style={{ background:theme.card, borderRadius:18, width:"100%", maxWidth:520,
            maxHeight:"90vh", overflowY:"auto",
            border:`1px solid ${theme.border}`, boxShadow:"0 24px 64px rgba(0,0,0,0.6)" }}>

            <div style={{ padding:"20px 24px", borderBottom:`1px solid ${theme.border}` }}>
              <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:theme.text }}>
                {editingOffer ? "編輯優惠" : "新增限時優惠"}
              </h2>
            </div>

            <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 }}>

              {/* Title */}
              <div>
                <label style={s.label}>優惠名稱 <span style={{ color:theme.danger }}>*</span></label>
                <input type="text" maxLength={50} placeholder="例：主廚特餐 限時優惠"
                  value={form.title}
                  onChange={e => setForm(f=>({...f, title:e.target.value}))}
                  style={{ ...s.input, ...(formErrors.title ? s.inputError : {}) }} />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                  {formErrors.title
                    ? <span style={{ fontSize:11, color:theme.danger }}>{formErrors.title}</span>
                    : <span />}
                  <span style={{ fontSize:11, color:theme.textDim }}>{form.title.length}/50</span>
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={s.label}>說明</label>
                <textarea rows={2} placeholder="例：每日限量・附湯品甜點"
                  value={form.description}
                  onChange={e => setForm(f=>({...f, description:e.target.value}))}
                  style={{ ...s.input, resize:"none" }} />
              </div>

              {/* Prices */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={s.label}>原價 (NT$)</label>
                  <input type="number" min="0" placeholder="0"
                    value={form.original_price}
                    onChange={e => setForm(f=>({...f, original_price:e.target.value}))}
                    style={s.input} />
                </div>
                <div>
                  <label style={s.label}>特價 (NT$) <span style={{ color:theme.danger }}>*</span></label>
                  <input type="number" min="1" placeholder="0"
                    value={form.flash_price}
                    onChange={e => setForm(f=>({...f, flash_price:e.target.value}))}
                    style={{ ...s.input, ...(formErrors.flash_price ? s.inputError : {}) }} />
                  {formErrors.flash_price && (
                    <span style={{ fontSize:11, color:theme.danger, marginTop:4, display:"block" }}>
                      {formErrors.flash_price}
                    </span>
                  )}
                </div>
              </div>

              {/* STEP 4 — discount preview + guardrails */}
              {discountInfo.pct > 0 && (
                <div style={{ padding:"10px 14px", borderRadius:8, fontSize:13,
                  background: discountInfo.guard === "block" ? theme.danger+"18"
                             : discountInfo.guard === "warn"  ? theme.warning+"18"
                             : theme.orange+"18",
                  color:       discountInfo.guard === "block" ? theme.danger
                             : discountInfo.guard === "warn"  ? theme.warning
                             : theme.orange,
                }}>
                  折扣：{discountInfo.pct}% off
                  {discountInfo.guard === "warn"  && " — ⚠️ 折扣超過五折，請確認"}
                  {discountInfo.guard === "block" && " — ❌ 折扣超過八折，需主管權限"}
                </div>
              )}

              {/* Margin guardrail — cost input (client-side only, never sent to server) */}
              <div>
                <label style={s.label}>
                  成本價 (NT$)
                  <span style={{ fontSize:11, color:theme.textDim, fontWeight:400, marginLeft:6 }}>
                    選填 · 僅供毛利試算，不會上傳
                  </span>
                </label>
                <input
                  type="number" min="0" placeholder="0"
                  value={form.base_cost}
                  onChange={e => setForm(f => ({ ...f, base_cost: e.target.value }))}
                  style={s.input}
                />
              </div>

              {/* Margin result banner */}
              {marginInfo && (
                <div style={{ borderRadius:8, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", fontSize:13, fontWeight:500,
                    background: marginInfo.guard === "ok"    ? theme.success+"1A"
                               : marginInfo.guard === "warn"  ? theme.warning+"1A"
                               : theme.danger+"1A",
                    color:       marginInfo.guard === "ok"    ? theme.success
                               : marginInfo.guard === "warn"  ? theme.warning
                               : theme.danger,
                  }}>
                    {marginInfo.label}
                  </div>
                  <div style={{ padding:"8px 14px", fontSize:12,
                    background: theme.cardHover, color: theme.textMuted }}>
                    最低可售價：<span style={{ color:theme.text, fontWeight:600 }}>NT${marginInfo.floorPrice.toLocaleString()}</span>
                    <span style={{ marginLeft:8, color:theme.textDim }}>(成本 × 1.15)</span>
                  </div>
                </div>
              )}

              {/* Stock */}
              <div>
                <label style={s.label}>數量 <span style={{ color:theme.danger }}>*</span></label>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <input type="number" min="1" placeholder="20"
                    value={form.stock_remaining} disabled={form.unlimited}
                    onChange={e => setForm(f=>({...f, stock_remaining:e.target.value}))}
                    style={{ ...s.input, ...(formErrors.stock_remaining ? s.inputError : {}),
                      opacity:form.unlimited ? 0.4 : 1, flex:1 }} />
                  <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13,
                    color:theme.textMuted, cursor:"pointer", whiteSpace:"nowrap" }}>
                    <input type="checkbox" checked={form.unlimited}
                      onChange={e => setForm(f=>({...f, unlimited:e.target.checked}))}
                      style={{ accentColor:theme.orange }} />
                    不限量
                  </label>
                </div>
                {formErrors.stock_remaining && (
                  <span style={{ fontSize:11, color:theme.danger, marginTop:4, display:"block" }}>
                    {formErrors.stock_remaining}
                  </span>
                )}
              </div>

              {/* Max/person + Duration */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={s.label}>每人限購</label>
                  <input type="number" min="1"
                    value={form.max_per_person}
                    onChange={e => setForm(f=>({...f, max_per_person:e.target.value}))}
                    style={s.input} />
                </div>
                <div>
                  <label style={s.label}>限時 <span style={{ color:theme.danger }}>*</span></label>
                  <select value={form.duration_minutes}
                    onChange={e => setForm(f=>({...f, duration_minutes:e.target.value}))}
                    style={{ ...s.input, ...(formErrors.duration_minutes ? s.inputError : {}), cursor:"pointer" }}>
                    {PRESET_DURATIONS.map(m => (
                      <option key={m} value={m}>{m} 分鐘</option>
                    ))}
                    <option value="custom">自訂</option>
                  </select>
                  {isCustomDuration && (
                    <input type="number" min="1" placeholder="輸入分鐘數"
                      value={form.duration_custom}
                      onChange={e => setForm(f=>({...f, duration_custom:e.target.value}))}
                      style={{ ...s.input, marginTop:8 }} />
                  )}
                  {formErrors.duration_minutes && (
                    <span style={{ fontSize:11, color:theme.danger, marginTop:4, display:"block" }}>
                      {formErrors.duration_minutes}
                    </span>
                  )}
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label style={s.label}>圖片 URL</label>
                <input type="url" placeholder="https://…"
                  value={form.image_url}
                  onChange={e => setForm(f=>({...f, image_url:e.target.value}))}
                  style={s.input} />
              </div>
            </div>

            <div style={{ padding:"16px 24px", borderTop:`1px solid ${theme.border}`,
              display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setShowModal(false)} style={s.btnGhost}
                onMouseEnter={e=>{ e.currentTarget.style.background = theme.cardHover; }}
                onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}>
                取消
              </button>
              {/* disabled when discount > 80% OR margin < 15% */}
              <button onClick={handleSubmit}
                disabled={submitting || discountInfo.guard === "block" || marginInfo?.guard === "block"}
                style={{ ...s.btnOrange,
                  opacity: (submitting || discountInfo.guard === "block" || marginInfo?.guard === "block") ? 0.45 : 1,
                  cursor:  (submitting || discountInfo.guard === "block" || marginInfo?.guard === "block") ? "not-allowed" : "pointer" }}
                onMouseEnter={e=>{ if (!submitting && discountInfo.guard !== "block" && marginInfo?.guard !== "block") e.currentTarget.style.background = theme.orangeHover; }}
                onMouseLeave={e=>{ e.currentTarget.style.background = theme.orange; }}>
                {submitting ? "處理中…" : editingOffer ? "儲存變更" : "建立優惠"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
